'use client'

import { isBefore } from 'date-fns'
import { isNull, isString } from 'lodash-es'
import {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  ComponentProps,
  useCallback,
  useEffect,
  useState,
  ReactNode,
  useRef,
  useImperativeHandle,
  RefObject,
} from 'react'

import {
  convertToStringDateRange,
  DateRange,
  DEFAULT_DATE_RANGE,
  isDateRange,
  isNilOrEmptyString,
  StringDateRange,
  cn,
  Slot,
  usePopover,
  useFormatDate,
} from '@/shared/lib'

import { Calendar, type Props as CalendarProps } from '../calendar'
import { Icon } from '../icon'
import { Input, TextField } from '../input'
import * as Popover from '../popover'

const CONTENT_OPTIONS = {
  align: 'start',
  sideOffset: 4,
} as const

export type RangeDatePickerRef = {
  focus: () => void
}

export type RangeDatePickerProps = Omit<CalendarProps, 'mode'> &
  Pick<
    ComponentProps<typeof Popover.Content>,
    'align' | 'alignOffset' | 'sideOffset'
  > &
  Omit<
    ComponentProps<typeof Input>,
    'value' | 'onChange' | 'min' | 'max' | 'ref'
  > & {
    value?: string
    onChange?: (dateRange?: StringDateRange) => void
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void
    onFocus?: (event: FocusEvent<HTMLButtonElement>) => void
    onBlur?: (event: FocusEvent<HTMLButtonElement>) => void
    onClose?: () => void
    onEnterKeyDown?: (event: KeyboardEvent<HTMLButtonElement>) => void
    openOnFocus?: boolean
    popoverClassName?: string
    render?: (props: {
      selected: DateRange | undefined
      onSelect: (dateRange?: DateRange) => void
      onClose: (...args: unknown[]) => void
    }) => ReactNode
    ref?: RefObject<RangeDatePickerRef>
    editable?: boolean
  }

type DefinedStringDateRange = { from: string; to: string }
type DefinedDateRange = { from: Date; to: Date }

export const isDefinedStringDateRange = (
  dateRange?: StringDateRange,
): dateRange is DefinedStringDateRange => {
  return !!dateRange?.from && !!dateRange?.to
}

const isDefinedDateRange = (
  dateRange?: DateRange,
): dateRange is DefinedDateRange => {
  return !!dateRange?.from && !!dateRange?.to
}

const isNotSelected = (dateRange?: DateRange): dateRange is undefined => {
  return !dateRange?.from && !dateRange?.to
}

const isBothSelected = (
  dateRange?: DateRange,
): dateRange is DefinedDateRange => {
  return !!dateRange?.from && !!dateRange?.to && dateRange.from !== dateRange.to
}

const isStartSelected = (
  dateRange?: DateRange,
): dateRange is DefinedDateRange => {
  return isDefinedDateRange(dateRange) && dateRange.from === dateRange.to
}

export const getNewDateRange = ({
  prevDateRange,
  nextDateRange,
}: {
  prevDateRange?: DateRange
  nextDateRange?: DateRange
}): DateRange | undefined => {
  // 1. 선택 해제
  if (!isDefinedDateRange(nextDateRange)) {
    return undefined
  }

  // 2. 시작일 선택
  // 2-1. 시작일 & 종료일 모두 미선택인 경우 선택한 날짜를 시작일로 선택
  if (isNotSelected(prevDateRange)) {
    return nextDateRange
  }

  // 2-2. 시작일 & 종료일 모두 선택 상태에서 선택한 날짜를 시작일로 선택
  if (isBothSelected(prevDateRange)) {
    const newStartDate =
      nextDateRange.from !== prevDateRange.from
        ? nextDateRange.from
        : nextDateRange.to

    return {
      from: newStartDate,
      to: newStartDate,
    }
  }

  if (isStartSelected(prevDateRange)) {
    // 3. 시작일 선택 상태에서 선택한 날짜
    // 3-1. 선택한 날짜가 시작일보다 과거인 경우: 시작일로 선택
    if (isBefore(nextDateRange.from, prevDateRange.from)) {
      return {
        from: nextDateRange.from,
        to: nextDateRange.from,
      }
    } else {
      // 3-2. 선택한 날짜가 시작일보다 미래인 경우: 종료일로 선택
      return nextDateRange
    }
  }
}

export const RangeDatePicker = ({
  value,
  onChange,
  placeholder,
  fullWidth,
  error,
  disabled,
  openOnFocus = false,
  className,
  onClick,
  onFocus,
  onBlur,
  onClose,
  onEnterKeyDown,
  children,
  align,
  alignOffset,
  sideOffset,
  render,
  popoverClassName,
  editable = true,
  padding,
  ref: refProp,
  ...props
}: RangeDatePickerProps) => {
  const { formatDateRange, parseDateRange } = useFormatDate()

  const { isOpen, ref, open, close, toggleOpen, handleOutsideClick } =
    usePopover<HTMLDivElement>({
      disabled: disabled,
      onClose,
    })

  useImperativeHandle(refProp, () => ({
    focus: () => {
      ref.current?.focus()
    },
  }))

  const mouseEventRef = useRef<boolean>(false)

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>()

  const [inputValue, setInputValue] = useState(value)

  const updateDate = useCallback((dateRange?: string | DateRange) => {
    if (isNilOrEmptyString(dateRange)) {
      setInputValue('')
      setSelectedDateRange(DEFAULT_DATE_RANGE)

      return
    }

    if (isDateRange(dateRange)) {
      setInputValue(formatDateRange({ dateRange }))
      setSelectedDateRange(dateRange)

      return
    }

    if (isString(dateRange)) {
      setInputValue(dateRange)
      setSelectedDateRange(parseDateRange({ dateRange }))

      return
    }
  }, [])

  const handleCustomRenderSelect = (dateRange?: DateRange) => {
    updateDate(dateRange)
    onChange?.(
      convertToStringDateRange({
        dateRange: dateRange,
        format: 'yyyy-MM-dd',
      }),
    )
  }

  const handleDayPickerSelect = (dateRange?: DateRange) => {
    const newDateRange = getNewDateRange({
      prevDateRange: selectedDateRange,
      nextDateRange: dateRange,
    })

    updateDate(newDateRange)
    onChange?.(
      convertToStringDateRange({
        dateRange: newDateRange,
        format: 'yyyy-MM-dd',
      }),
    )
  }

  const handleInputReset = () => {
    setInputValue('')
    setSelectedDateRange(DEFAULT_DATE_RANGE)
    onChange?.(DEFAULT_DATE_RANGE)
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value) // keep the input value in sync

    const parsedDateRange = parseDateRange({ dateRange: event.target.value })

    setSelectedDateRange(parsedDateRange)
    onChange?.(
      convertToStringDateRange({
        dateRange: parsedDateRange,
        format: 'yyyy-MM-dd',
      }),
    )
  }

  const handleTriggerMouseDown = () => {
    mouseEventRef.current = true
  }

  const handleTriggerMouseUp = () => {
    mouseEventRef.current = false
  }

  const handleTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    toggleOpen()
    onClick?.(event)
  }

  const handleTriggerFocus = (event: FocusEvent<HTMLButtonElement>) => {
    const triggeredByOutside =
      !isNull(event.relatedTarget) &&
      !ref.current?.contains(event.relatedTarget as Node)

    if (mouseEventRef.current) {
      return
    }

    if (openOnFocus && triggeredByOutside) {
      open()
    }

    onFocus?.(event)
  }

  const handleTriggerBlur = (event: FocusEvent<HTMLButtonElement>) => {
    if (selectedDateRange) {
      setInputValue(formatDateRange({ dateRange: selectedDateRange }))
    }

    onBlur?.(event)
  }

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      onEnterKeyDown?.(event)
    }
  }

  useEffect(() => {
    if (value === formatDateRange({ dateRange: selectedDateRange })) return

    updateDate(value)
  }, [updateDate, value])

  return (
    <Popover.Root open={isOpen}>
      <Popover.Trigger
        asChild
        onClick={handleTriggerClick}
        tabIndex={0}
        onFocus={handleTriggerFocus}
        onBlur={handleTriggerBlur}
        onMouseDown={handleTriggerMouseDown}
        onMouseUp={handleTriggerMouseUp}
        onKeyDown={handleTriggerKeyDown}
      >
        <div className={cn(fullWidth && 'w-full', className)} ref={ref}>
          <TextField
            value={inputValue}
            placeholder={placeholder}
            onChange={handleInputChange}
            fullWidth
            error={error}
            readOnly={true}
            disabled={disabled}
            tabIndex={-1}
            onReset={handleInputReset}
            editable={editable}
            type="text"
            className={cn(
              'cursor-pointer border-none bg-[rgba(0,0,0,0)] text-[15px]! leading-[22px]!',
              '*:placeholder:text-gray-400 *:read-only:cursor-pointer',
            )}
            padding={{
              x: 16,
              y: 13,
              ...padding,
            }}
          >
            <Slot name="left">
              <Icon
                name="calendar"
                className="text-black"
                size="24"
                strokeWidth={1.5}
              />
            </Slot>
            {children}
          </TextField>
        </div>
      </Popover.Trigger>
      <Popover.Content
        closeButton={false}
        arrow={false}
        onInteractOutside={handleOutsideClick}
        onEscapeKeyDown={close}
        className={popoverClassName}
        {...CONTENT_OPTIONS}
        {...(align && { align })}
        {...(alignOffset && { alignOffset })}
        {...(sideOffset && { sideOffset })}
      >
        {(() => {
          if (render) {
            return render({
              selected: selectedDateRange,
              onSelect: handleCustomRenderSelect,
              onClose: close,
            })
          }

          return (
            <Calendar
              {...props}
              mode="range"
              numberOfMonths={2}
              required={false}
              className="p-2 shadow-none"
              selected={selectedDateRange}
              onSelect={handleDayPickerSelect}
            />
          )
        })()}
      </Popover.Content>
    </Popover.Root>
  )
}
